import React from "react";

import { Form } from "semantic-ui-react";

import TimeComponent from "@common/components/shared/forms/TimeComponent";
import CheckboxComponent from "@common/components/shared/forms/CheckboxComponent";

/**
 * DidNotAttendTimeGroup renders a time component and did not attend checkbox with validation.
 *
 * @typedef {object} DidNotAttendTimeProps
 * @property {Function} control - Function to control the time field for form
 * @property {string} timeName - Name of the time field.
 * @property {string} didNotAttendName - Name of the checkbox field.
 * @property {string} [label=''] - Label of the field.
 * @property {string} [timeLabel=''] - Label of the time field.
 * @property {Object} [timeError=null] - Object containing validation error.
 * @property {Object} [didNotAttendError=null] - Object containing validation error.
 * @property {string} [timeFormat='HH:mm'] - The format of the time.
 * @property {string} [fieldStyle='flex-between flex-row-center'] - Style for Form.Field.
 * @property {string} [labelStyle='w-30'] - Style for the label.
 * @property {string} [checkboxFieldStyle='flex-row-center m-0'] - Style for checkbox field.
 * @property {string} [checkboxLabelStyle] - Style for checkbox label.
 * @property {string} [checkboxContainerStyle] - Style for checkbox container.
 * @property {string} [timeFieldStyle] - Style for time field.
 * @property {string} [timeLabelStyle] - Style for time label.
 * @property {string} [timeContainerStyle] - Style for time container.
 * @property {Function} [setValue] - Function to set the value of a form field.
 * @property {boolean} [didNotAttend] - Boolean to watch did not attend.
 *
 * @component
 * @param {DidNotAttendTimeProps} props - The component props.
 * @returns {JSX.Element} The rendered time input and did not attend checkbox with validation.
 */

export default function DidNotAttendTimeGroup({
  control,
  timeName,
  didNotAttendName,
  label = "",
  timeLabel = "",
  timeError = null,
  didNotAttendError = null,
  timeFormat,
  fieldStyle = "flex-between flex-row-center",
  labelStyle = "w-30",
  checkboxFieldStyle = "flex-row-center m-0",
  checkboxLabelStyle,
  checkboxContainerStyle,
  timeFieldStyle,
  timeLabelStyle,
  timeContainerStyle,
  setValue,
  didNotAttend = false,
}) {
  return (
    <Form.Field className={`${fieldStyle}`}>
      {label && <label className={`${labelStyle}`}>{label}</label>}
      <CheckboxComponent
        control={control}
        name={didNotAttendName}
        label="Did not attend"
        error={didNotAttendError}
        fieldStyle={checkboxFieldStyle}
        labelStyle={checkboxLabelStyle}
        checkboxContainerStyle={checkboxContainerStyle}
        handleChange={() => setValue(timeName, "")}
      />
      <TimeComponent
        control={control}
        name={timeName}
        timeLabel={timeLabel}
        timeFormat={timeFormat}
        fieldStyle={`${timeFieldStyle}`}
        labelStyle={timeLabelStyle}
        error={timeError}
        timeContainerStyle={timeContainerStyle}
        disabled={didNotAttend}
      />
    </Form.Field>
  );
}
